import { Injectable, inject, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AIEffectiveSettings, AISettingsAction, AISettingsEventType } from '@jms/model';
import { SharedSocketService } from '@workbuddy/services';
import { TenantRoomSocketService } from '@workbuddy/services/socket/tenant.room.socket.service';
import { catchError, defer, EMPTY, filter, from, map, merge, of, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AISettingsService {

  private readonly socket = inject(SharedSocketService);
  private readonly tenantRoom = inject(TenantRoomSocketService);

  // connected$ is a plain Subject, so lazy-instantiated subscribers miss the connect event; defer covers that case.
  // Each socket connect (initial + reconnect) re-dispatches GetEffectiveSettings. On transient
  // errors or null responses we deliberately do NOT emit — that would reset the signal to an
  // all-false default and briefly hide AI features (e.g. the bill scanner card) until the next
  // successful load. `toSignal` keeps the previous value when the source doesn't emit, which is
  // the right fallback for "the last load succeeded; this reconnect-load happened to fail".
  private readonly load$ = merge(
    defer(() => this.socket.isConnected ? of(undefined) : EMPTY),
    this.socket.connected$,
  ).pipe(
    switchMap(() => from(this.socket.dispatch<AIEffectiveSettings>(AISettingsAction.GetEffectiveSettings, {}))
      .pipe(
        filter((s): s is AIEffectiveSettings => !!s),
        catchError(() => EMPTY),
      )),
  );

  private readonly updates$ = this.tenantRoom.events$
    .pipe(
      filter((msg: any) => msg.type === AISettingsEventType.EffectiveUpdated),
      map((msg: any) => msg.payload as AIEffectiveSettings),
    );

  private readonly $settings = toSignal<AIEffectiveSettings | null>(
    merge(this.load$, this.updates$),
    { initialValue: null },
  );

  public readonly $loaded = computed(() => this.$settings() !== null);
  public readonly $aiEnabled = computed(() => this.$settings()?.enabled ?? false);
  public readonly $taskValidationEnabled = computed(() => this.$settings()?.agents?.taskValidation ?? false);
  public readonly $billImportEnabled = computed(() => this.$settings()?.agents?.billImport ?? false);
}
